import React, { useState, useEffect } from 'react';
import './Dropdown.css';
import LoadingOverlay from '../components/LoadingOverlay';
import FormularioProduto from '../Formulario/FormularioProduto';
import PopupImportacaoProdutosEmMassa from '../Formulario/PopupImportacaoProdutosEmMassa';
import FotoProdutoThumb from '../components/FotoProdutoThumb';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';

// Helper: listens for MobileFooter generic + event
function MobileAddListener({ onOpen }) {
  useEffect(() => {
    window.addEventListener('open-mobile-add-popup', onOpen);
    return () => window.removeEventListener('open-mobile-add-popup', onOpen);
  }, [onOpen]);
  return null;
}

export default function ProdutoDropdown() {
  const [produtos, setProdutos] = useState([]);
  const [produtosFiltrados, setProdutosFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);
  // Muda a cada salvar: o card recarrega a foto (ela pode ter sido trocada no formulário)
  const [versaoFotos, setVersaoFotos] = useState(0);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProduto, setEditingProduto] = useState(null);
  
  // Estados para filtro
  const [filtro, setFiltro] = useState('id');
  const [valorFiltro, setValorFiltro] = useState('');
  const [dropdownAberto, setDropdownAberto] = useState(false);
  
  // Estado para popup importação em massa
  const [mostrarImportacaoMassa, setMostrarImportacaoMassa] = useState(false);

  // Opções de filtro
  const opcoesFiltro = [
    { valor: 'id', label: 'ID do Produto' },
    { valor: 'nome', label: 'Nome do Produto' },
    { valor: 'titulo', label: 'Título do Produto' }
  ];

  useEffect(() => {
    fetchProdutos();
  }, []);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('produto-popup-state', {
      detail: { formularioAberto: showForm }
    }));
  }, [showForm]);

  useEffect(() => {
    const handler = () => {
      setShowForm(false);
      setEditingProduto(null);
      setDropdownAberto(false);
      setMostrarImportacaoMassa(false);
    };
    window.addEventListener('produto-fechar-todos-popups', handler);
    return () => window.removeEventListener('produto-fechar-todos-popups', handler);
  }, []);

  // Função para fazer chamadas da API com fallback de URLs
  const apiCall = async (endpoint, options = {}) => {
    const urls = [
      `${BASE_URL}/${endpoint}`,
      `${BASE_URL}/${endpoint}`,
      `${BASE_URL}/${endpoint}`,
      `${BASE_URL}/${endpoint}`,
      `${BASE_URL}/${endpoint}`
    ];

    const token = getAuthToken();
    if (token) {
      options = { ...options };
      options.headers = { ...options.headers, Authorization: options.headers?.Authorization || `Bearer ${token}` };
    }

    for (const url of urls) {
      try {
        console.log(`Tentando conectar em: ${url}`);
        const response = await fetch(url, options);
        if (response.ok) {
          console.log(`✅ Sucesso em: ${url}`);
          return await response.json();
        }
        console.log(`❌ Falha em: ${url} - Status: ${response.status}`);
      } catch (error) {
        console.log(`❌ Erro em: ${url} - ${error.message}`);
      }
    }
    
    throw new Error('Nenhuma URL da API funcionou. Verifique se o backend está rodando.');
  };

  const fetchProdutos = async () => {
    try {
      setLoading(true);
      const data = await apiCall('Produto');
      setProdutos(data);
      setProdutosFiltrados(data);
      setError('');
    } catch (err) {
      console.error('Erro ao buscar produtos:', err);
      setError('Erro ao carregar produtos. Verifique se o backend está rodando.');
      setProdutos([]);
      setProdutosFiltrados([]);
    } finally {
      setLoading(false);
    }
  };

  // Funções de filtro
  const handleFiltroChange = (novoFiltro) => {
    setFiltro(novoFiltro);
    setDropdownAberto(false);
    setValorFiltro('');
    // Resetar para todos os produtos quando muda o filtro
    setProdutosFiltrados(produtos);
  };

  const handleInputChange = (e) => {
    const valor = e.target.value;
    setValorFiltro(valor);
    aplicarFiltro(valor);
  };

  const aplicarFiltro = (valor) => {
    if (!valor.trim()) {
      setProdutosFiltrados(produtos);
      return;
    }

    const filtrados = produtos.filter(produto => {
      if (filtro === 'id') {
        return produto.id && produto.id.toString().includes(valor);
      }
      if (filtro === 'nome') {
        return produto.nome && produto.nome.toLowerCase().includes(valor.toLowerCase());
      }
      if (filtro === 'titulo') {
        return produto.titulo && produto.titulo.toLowerCase().includes(valor.toLowerCase());
      }
      return false;
    });

    setProdutosFiltrados(filtrados);
  };

  const limparFiltro = () => {
    setValorFiltro('');
    setProdutosFiltrados(produtos);
  };

  const handleEdit = (produto) => {
    setEditingProduto(produto);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este produto?')) {
      try {
        const urls = [
          `${BASE_URL}/Produto/${id}`,
          `${BASE_URL}/Produto/${id}`,
          `${BASE_URL}/Produto/${id}`,
          `${BASE_URL}/Produto/${id}`,
          `${BASE_URL}/Produto/${id}`
        ];

        let success = false;
        for (const url of urls) {
          try {
            console.log(`Tentando deletar em: ${url}`);
            const response = await fetch(url, { method: 'DELETE' });
            if (response.ok) {
              console.log(`✅ Sucesso ao deletar em: ${url}`);
              success = true;
              break;
            }
          } catch (error) {
            console.log(`❌ Erro ao deletar em: ${url} - ${error.message}`);
          }
        }

        if (success) {
          fetchProdutos();
          alert('Produto excluído com sucesso!');
        } else {
          alert('Erro ao excluir produto. Verifique se o backend está rodando.');
        }
      } catch (err) {
        console.error('Erro ao excluir produto:', err);
        alert('Erro ao excluir produto');
      }
    }
  };

  const handleFormSalvar = () => {
    setVersaoFotos(v => v + 1);
    fetchProdutos();
    setShowForm(false);
    setEditingProduto(null);
  };

  const handleFormCancelar = () => {
    setShowForm(false);
    setEditingProduto(null);
  };

  return (
    <div className="container-geral fundo-branco" style={{ 
      minHeight: '100vh', 
      background: '#f0f0f0', 
      padding: '20px',
      position: 'relative'
    }}>
      
      <MobileAddListener onOpen={() => setShowForm(!showForm)} />

      {/* Botões de ação: Adicionar + Importar em Massa */}
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
        <button
          style={{
            background: 'rgb(235, 133, 0)',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            padding: '16px 32px',
            fontSize: 20,
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transition: 'background 0.2s'
          }}
          onMouseEnter={e => e.target.style.background = 'rgb(208, 116, 0)'}
          onMouseLeave={e => e.target.style.background = 'rgb(235, 133, 0)'}
          onClick={() => setShowForm(!showForm)}
        >
          Adicionar Produto
        </button>

        <button
          title="Cadastro em Massa via Excel"
          onClick={() => setMostrarImportacaoMassa(true)}
          style={{
            background: 'linear-gradient(135deg, #43a047, #2e7d32)',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            padding: '14px 24px',
            fontSize: 15,
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(67,160,71,0.3)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            transition: 'filter 0.2s'
          }}
          onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'}
          onMouseLeave={e => e.currentTarget.style.filter = 'brightness(1)'}
        >
          📥 Cadastro em Massa
        </button>
      </div>

      {/* Filtro de produtos */}
      <div style={{
        width: '100%',
        maxWidth: 1200,
        margin: '0 auto 24px auto',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        padding: '0 20px',
        justifyContent: 'center'
      }} className="filtro-container-produto">
        <label className="filtro-label-produto" style={{ 
          color: '#333', 
          fontSize: '16px', 
          fontWeight: '500',
          whiteSpace: 'nowrap'
        }}>
          Filtrar por:
        </label>
        <div className="filtro-campos-produto" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <input
              type="text"
              className="filtro-input-produto"
              style={{
                padding: '10px 15px',
                paddingRight: valorFiltro ? '40px' : '15px',
                border: '1px solid #ccc',
                borderRadius: '5px',
                fontSize: '14px',
                minWidth: '250px',
                outline: 'none'
              }}
              placeholder={`Filtrar por ${opcoesFiltro.find(opcao => opcao.valor === filtro)?.label || 'campo'}`}
              value={valorFiltro}
              onChange={handleInputChange}
            />
            {valorFiltro && (
              <button
                onClick={limparFiltro}
                title="Limpar filtro"
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  color: 'rgb(235, 133, 0)',
                  border: 'none',
                  width: '20px',
                  height: '20px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  lineHeight: '1',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  outline: 'none',
                  boxShadow: 'none'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'rgb(235, 133, 0)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'rgb(235, 133, 0)';
                }}
                onFocus={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'rgb(235, 133, 0)';
                  e.target.style.outline = 'none';
                }}
              >
                ×
              </button>
            )}
          </div>
          <div className="filtro-select-container-produto" style={{ position: 'relative' }}>
            <button
              className="filtro-select-btn-produto"
              onClick={() => setDropdownAberto(!dropdownAberto)}
              style={{
                padding: '10px 15px',
                backgroundColor: 'rgb(235, 133, 0)',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                minWidth: '200px',
                justifyContent: 'space-between'
              }}
            >
              {opcoesFiltro.find(opcao => opcao.valor === filtro)?.label || 'Selecione'}
              <span style={{ transform: dropdownAberto ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
            </button>
            {dropdownAberto && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'rgb(235, 133, 0)',
                border: '1px solid rgb(235, 133, 0)',
                borderRadius: '5px',
                marginTop: '2px',
                zIndex: 1000,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                {opcoesFiltro.map(opcao => (
                  <div 
                    key={opcao.valor}
                    onClick={() => handleFiltroChange(opcao.valor)}
                    style={{ 
                      padding: '10px 15px', 
                      cursor: 'pointer',
                      borderBottom: '1px solid rgba(255,255,255,0.2)',
                      color: 'white'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    {opcao.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Formulário de produto */}
      {showForm && (
        <div style={{
          width: '100%',
          maxWidth: 1200,
          margin: '0 auto 24px auto',
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <FormularioProduto
            produto={editingProduto}
            onSalvar={handleFormSalvar}
            onCancelar={handleFormCancelar}
          />
        </div>
      )}

      {/* Popup Importação em Massa */}
      {mostrarImportacaoMassa && (
        <PopupImportacaoProdutosEmMassa
          onFechar={() => setMostrarImportacaoMassa(false)}
          onSucesso={() => {
            fetchProdutos();
            setMostrarImportacaoMassa(false);
          }}
        />
      )}

      {/* Loading */}
      {loading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#666',
          backgroundColor: 'white',
          borderRadius: '8px',
          margin: '0 auto',
          maxWidth: 1200
        }}>
          Carregando produtos...
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#d32f2f',
          backgroundColor: 'white',
          borderRadius: '8px',
          margin: '0 auto',
          maxWidth: 1200
        }}>
          {error}
        </div>
      )}

      {/* Lista de produtos */}
      {!loading && !error && (
        <div className="produto-cards-container" style={{ maxWidth: 1200, margin: '0 auto' }}>
          {produtosFiltrados.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: '#666',
              backgroundColor: 'white',
              borderRadius: '8px'
            }}>
              {valorFiltro ? 'Nenhum produto encontrado com este filtro.' : 'Nenhum produto cadastrado.'}
            </div>
          ) : (
            produtosFiltrados.map((produto) => (
              <div key={produto.id} className="produto-card">
                <div className="produto-card-content">
                  {/* Nome do produto em laranja; embaixo a foto e, à direita dela, os dados; o preço destacado no fim */}
                  <div className="produto-card-header">
                    {produto.nome || `Produto #${produto.id}`}
                  </div>
                  <div className="produto-card-corpo">
                    <FotoProdutoThumb produtoId={produto.id} temFoto={produto.temFoto} versao={versaoFotos} tamanho={220} />
                    <div className="produto-card-fields produto-card-fields-compactos">
                      <div className="produto-field produto-field-id">
                        <span className="produto-field-label">ID:</span>
                        <span className="produto-field-value">{produto.id}</span>
                      </div>
                      <div className="produto-field produto-field-categoria">
                        <span className="produto-field-label">Categoria:</span>
                        <span className="produto-field-value">{produto.categoria || 'N/A'}</span>
                      </div>
                      <div className="produto-field produto-field-estoque">
                        <span className="produto-field-label">Estoque:</span>
                        <span className="produto-field-value">
                          {typeof produto.estoqueFisico === 'number' && typeof produto.estoqueReservado === 'number'
                            ? produto.estoqueFisico - produto.estoqueReservado
                            : (parseInt(produto.estoqueFisico) || 0) - (parseInt(produto.estoqueReservado) || 0)}
                          {produto.unidade ? ` ${produto.unidade}` : ''}
                        </span>
                      </div>
                      <div className="produto-field produto-field-status">
                        <span className="produto-field-label">Status:</span>
                        <span className="produto-field-value" style={{
                          color: produto.ativo ? '#4caf50' : '#f44336'
                        }}>
                          {produto.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                      {/* Preço em destaque logo abaixo do Status, alinhado com os outros dados */}
                      <div className="produto-card-preco">
                        {`R$ ${(Number(produto.preco) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="produto-card-actions">
                  <button
                    className="btn-editar-produto"
                    onClick={() => handleEdit(produto)}
                    title="Editar produto"
                  >
                    Editar
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <LoadingOverlay isLoading={loading} message="Processando..." />
    </div>
  );
}
